import React, { useCallback, useEffect, useRef, useState } from "react";
import { XMarkIcon, MinusIcon, PlusIcon, ArrowPathIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export default function ZoomableImageModal({
  isOpen,
  images = [],
  index = 0,
  onClose,
  onPrev,
  onNext,
  showNavigation = false,
  onBackgroundMouseMove,
  onBackgroundMouseLeave,
  sidePanel,
  showZoomControls = true,
  controlsPosition = "right", // "left" | "right" — use "left" when side panel/hover blocks right (e.g. TradesPage)
}) {
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const dragRef = useRef({ active: false, startX: 0, startY: 0, baseX: 0, baseY: 0 });
  const pinchRef = useRef({ distance: null, scale: 1, center: null, baseTranslate: { x: 0, y: 0 } });

  const resetTransform = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    if (isOpen) {
      resetTransform();
    }
  }, [isOpen, index, resetTransform]);

  const handleWheel = useCallback((event) => {
    event.preventDefault();
    const delta = event.deltaY < 0 ? 0.12 : -0.12;
    setScale((prev) => {
      const next = clamp(prev + delta, 1, 4);
      if (next === 1) {
        setTranslate({ x: 0, y: 0 });
      }
      return next;
    });
  }, []);

  const handleMouseDown = useCallback((event) => {
    if (scale === 1) return;
    event.preventDefault();
    dragRef.current = {
      active: true,
      startX: event.clientX,
      startY: event.clientY,
      baseX: translate.x,
      baseY: translate.y,
    };
  }, [scale, translate.x, translate.y]);

  const handleMouseMove = useCallback((event) => {
    if (!dragRef.current.active) return;
    const dx = event.clientX - dragRef.current.startX;
    const dy = event.clientY - dragRef.current.startY;
    setTranslate({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
  }, []);

  const handleMouseUp = useCallback(() => {
    dragRef.current.active = false;
  }, []);

  const handleTouchStart = useCallback((event) => {
    if (event.touches.length === 1) {
      if (scale === 1) return;
      const touch = event.touches[0];
      dragRef.current = {
        active: true,
        startX: touch.clientX,
        startY: touch.clientY,
        baseX: translate.x,
        baseY: translate.y,
      };
    } else if (event.touches.length === 2) {
      const [t1, t2] = event.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      pinchRef.current.distance = Math.hypot(dx, dy);
      pinchRef.current.scale = scale;
      pinchRef.current.baseTranslate = { ...translate };
      pinchRef.current.center = {
        x: (t1.clientX + t2.clientX) / 2,
        y: (t1.clientY + t2.clientY) / 2,
      };
    }
  }, [scale, translate]);

  const handleTouchMove = useCallback((event) => {
    if (event.touches.length === 1 && dragRef.current.active) {
      const touch = event.touches[0];
      const dx = touch.clientX - dragRef.current.startX;
      const dy = touch.clientY - dragRef.current.startY;
      setTranslate({ x: dragRef.current.baseX + dx, y: dragRef.current.baseY + dy });
      return;
    }
    if (event.touches.length === 2 && pinchRef.current.distance) {
      event.preventDefault();
      const [t1, t2] = event.touches;
      const dx = t2.clientX - t1.clientX;
      const dy = t2.clientY - t1.clientY;
      const distance = Math.hypot(dx, dy);
      const ratio = distance / pinchRef.current.distance;
      const nextScale = clamp(pinchRef.current.scale * ratio, 1, 4);
      setScale(nextScale);
      if (nextScale === 1) {
        setTranslate({ x: 0, y: 0 });
      } else if (pinchRef.current.center) {
        setTranslate(pinchRef.current.baseTranslate);
      }
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    dragRef.current.active = false;
    pinchRef.current.distance = null;
  }, []);

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[index];

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center"
      onMouseMove={(e) => {
        handleMouseMove(e);
        onBackgroundMouseMove?.(e);
      }}
      onMouseUp={handleMouseUp}
      onMouseLeave={(e) => {
        handleMouseUp(e);
        onBackgroundMouseLeave?.(e);
      }}
      onClick={onClose}
    >
      <div className="absolute inset-0" />
      <div
        className={`absolute top-4 z-20 flex items-center gap-2 ${controlsPosition === "left" ? "left-4" : "right-4"}`}
      >
        {showZoomControls && <button
          onClick={(e) => { e.stopPropagation(); setScale((prev) => clamp(prev - 0.2, 1, 4)); }}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          aria-label="Zoom out"
        >
          <MinusIcon className="w-5 h-5" />
        </button>}
        {showZoomControls && <button
          onClick={(e) => { e.stopPropagation(); setScale((prev) => clamp(prev + 0.2, 1, 4)); }}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          aria-label="Zoom in"
        >
          <PlusIcon className="w-5 h-5" />
        </button>}
        {showZoomControls && <button
          onClick={(e) => { e.stopPropagation(); resetTransform(); }}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          aria-label="Reset zoom"
        >
          <ArrowPathIcon className="w-5 h-5" />
        </button>}
        <button
          onClick={(e) => { e.stopPropagation(); onClose?.(); }}
          className="w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
          aria-label="Close image"
        >
          <XMarkIcon className="w-6 h-6" />
        </button>
      </div>

      {showNavigation && images.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev?.(); }}
            className="absolute left-4 z-20 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
            aria-label="Previous image"
          >
            <ChevronLeftIcon className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext?.(); }}
            className="absolute right-4 z-20 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition-colors"
            aria-label="Next image"
          >
            <ChevronRightIcon className="w-6 h-6" />
          </button>
        </>
      )}

      <div
        className="relative max-w-[95vw] max-h-[90vh] w-full h-full flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onDoubleClick={resetTransform}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
        style={{ touchAction: "none" }}
      >
        <img
          src={currentImage}
          alt="Expanded"
          className="max-w-full max-h-full object-contain select-none"
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
            transition: dragRef.current.active ? "none" : "transform 120ms ease-out",
            cursor: scale > 1 ? "grab" : "default",
          }}
          draggable={false}
        />
      </div>

      {sidePanel}
    </div>
  );
}
